<template>
  <div class="modal-parent">
    <div
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <div class="modal_topbar">
        <div class="modal_topbar-title">
          Remote Authorization - {{ device.name }}
        </div>
        <span
          id="exit-group-modal"
          class="modal_topbar-exit"
          @click="closeModal"
          v-html="closeIcon"
        />
      </div>
      <div class="form-container mb-0">
        In the event of an emergency, you can generate a one-time code that can be entered at a
        PIN-enabled access point to gain access.
        <div>
          <div>
            <h4 class="mb-0 mt-2">
              STEP 1
            </h4>
            <hr class="solid" />
            Identify the type of device at this access point and enter the 4 digit code.
            <div class="flex-grid mt-1">
              <div class="column--two device-type-container">
                <h3>HID Device</h3>
                <div class="flex-grid flex-grid--justify-start">
                  <div class="key-box">
                    <img
                      :src="asteriskIcon"
                      class="key-box__icon"
                    />
                  </div>
                  <div
                    class="key-box"
                    v-html="poundIcon"
                  />
                  <div
                    class="key-box"
                    v-html="poundIcon"
                  />
                  <div
                    class="key-box"
                    v-html="poundIcon"
                  />
                </div>
              </div>
              <div class="column--two device-type-container">
                <h3>ICT Device</h3>
                <div class="flex-grid flex-grid--justify-start">
                  <div
                    class="key-box"
                    v-html="xIcon"
                  />
                  <div
                    class="key-box"
                    v-html="enterIcon"
                  />
                  <div
                    class="key-box"
                    v-html="enterIcon"
                  />
                  <div
                    class="key-box"
                    v-html="enterIcon"
                  />
                </div>
              </div>
            </div>
            <h4 class="mb-0 mt-2">
              STEP 2
            </h4>
            <hr class="solid" />
            Immediately after entering the code above, enter the following 6 digit code.
            <div class="flex-grid flex-grid--justify-center">
              <div>
                <div
                  class="code-container"
                  :class="{ 'code-expired': expired }"
                >
                  <span v-if="code !== ''">{{ code }}</span>
                  <span v-else>------</span>
                </div>
              </div>
              <div class="column--one">
                <div class="timer-container">
                  <template v-if="loading">
                    <loader :size="35" />
                  </template>
                  <div
                    v-else
                    class="timer"
                    :class="{ expiring: expiring }"
                  >
                    <div v-if="!expired">
                      {{ counterInMinSec }}
                    </div>
                    <div v-if="expired">
                      Code has expired
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal_footer modal_footer--center mt-0">
        <button
          id="remote-auth-generate"
          class="btn"
          :disabled="isButtonDisabled"
          @click="generateNewCode()"
        >
          Display Code
        </button>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
</template>

<script>
import deviceQueries from '@/graphql/devices.js'
import asteriskIcon from '@/assets/enter-mac-key 4star.svg'

import feather from 'feather-icons'
import Loader from './loader'

export default {
  name: 'RemoteAuthorizeModal',
  components: {
    loader: Loader,
  },
  mixins: [deviceQueries],
  props: ['device'],
  data() {
    return {
      closeIcon: feather.icons.x.toSvg(),
      xIcon: feather.icons.x.toSvg(),
      asteriskIcon: asteriskIcon,
      poundIcon: feather.icons.hash.toSvg(),
      enterIcon: feather.icons['corner-down-left'].toSvg(),
      code: '',
      loading: false,
      intervalObject: '',
      count: 0,
      expired: false,
      counterInMinSec: '',
    }
  },
  computed: {
    expiring() {
      const lastSeconds = 30
      return this.count > 0 && this.count <= lastSeconds
    },
    isButtonDisabled() {
      return this.loading || (this.code !== '' && !this.expired)
    },
  },
  beforeDestroy() {
    this.stopCounter()
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },
    async generateNewCode() {
      this.loading = true
      this.code = ''
      this.expired = false

      try {
        const {otp, secondsRemaining} = await this.generateOtpMutation(this.device.serialNumber)

        this.code = otp
        this.count = secondsRemaining

        this.startCountDown()
      } catch (error) {
        console.log(error)
        this.displayAlert('fail', 'Unable to generate OTP.')
      } finally {
        this.loading = false
      }
    },
    updateElements() {
      this.count--
      this.setCounterString()
      if (this.count <= 0) {
        this.expired = true
        this.stopCounter()
      }
    },
    startCountDown() {
      this.stopCounter()
      this.updateElements()
      const secondInMs = 1000
      this.intervalObject = setInterval(this.updateElements, secondInMs)
    },
    setCounterString() {
      const secondsInMinute = 60
      const minutes = Math.floor((this.count / secondsInMinute) % secondsInMinute)
      const seconds = Math.floor(this.count % secondsInMinute)

      this.counterInMinSec = ''
      this.counterInMinSec += minutes > 0 ? `${minutes}min` : ''
      this.counterInMinSec += ` ${seconds}sec`
    },
    stopCounter() {
      clearInterval(this.intervalObject)
    },
    delay(time) {
      return new Promise((resolve) => {
        setTimeout(resolve, time)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.timer-container {
  min-height: 50px;
}
.timer {
  text-align: center;
  font-size: 1.5rem;
  color: $grey-text;
  &.expiring {
    color: $red;
    animation: blink-animation 1s steps(5, start) infinite;
    -webkit-animation: blink-animation 1s steps(5, start) infinite;
  }
}
.device-type-container {
  background-color: $off-white;
  box-sizing: border-box;
  padding: 1em;
  h3 {
    margin-top: 0;
    margin-bottom: 0.5em;
  }
  .key-box {
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
    width: 24px;
    height: 24px;
    background-color: black;
    padding: 10px;
    color: white;
    margin: 0 4px;
    border-radius: 3px;
    &__icon {
      width: 32px;
    }
  }
}
.code-container {
  border: 2px solid $dark-grey;
  height: 50px;
  width: 300px;
  margin: 2.5rem 0 1.5rem 0;
  text-align: center;
  font-size: 2.5rem;
  letter-spacing: 10px;
}
.code-expired {
  color: $grey-text;
  text-decoration: line-through;
}
@keyframes blink-animation {
  to {
    visibility: hidden;
  }
}
@-webkit-keyframes blink-animation {
  to {
    visibility: hidden;
  }
}
</style>
